import { useMemo } from "react"
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  getFilteredRowModel,
  getFacetedUniqueValues,
  getFacetedRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table"
import { DndContext, closestCenter } from "@dnd-kit/core"
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable"

import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@components/ui/table"
import { Skeleton } from "@components/ui/skeleton"
import { equalsOneOf } from "@utils/table/columnFilters"
import { useStableValue } from "@hooks/useStableValue"
import { cn } from "@components/ui/lib/utils"

import { DataTableToolbar } from "./DataTableToolbar"
import { ServerDataTableToolbar } from "./ServerDataTableToolbar"
import { DataTablePagination } from "./DataTablePagination"
import { DataTableViewOptions } from "./DataTableViewOptions"
import { DraggableTableHead } from "./DraggableTableHead"
import { DragAlongCell } from "./DragAlongCell"
import { useDndSetup } from "./useDndSetup"
import { DEFAULT_COLUMN_CONFIG } from "./constants"
import type { DataTableProps } from "./types"

export function DataTable<TData, TValue>({
  columns,
  data,
  classes,
  pending,
  sorting,
  onSortingChange,
  rowSelection,
  setRowSelection,
  filters,
  columnFilters,
  onColumnFiltersChange,
  condensed = false,
  noResultsMessage,
  paginationType = false,
  rowCount,
  pagination,
  onPaginationChange,
  onRowClick,
  serverFilters,
  onServerFiltersChange,
  onResetServerFilters,
  showViewOptions = false,
  columnVisibility,
  onColumnVisibilityChange,
  columnSizing,
  onColumnSizingChange,
  enableColumnResizing = !!columnSizing,
  columnResizeMode = "onChange",
  columnOrder,
  onColumnOrderChange,
  variant,
}: DataTableProps<TData, TValue>) {
  const tableData = useMemo(
    () => (pending ? Array(10).fill({}) : data),
    [pending, data],
  )

  const tableColumns = useMemo(
    () =>
      pending
        ? columns.map((column) => ({
            ...column,
            cell: () => <Skeleton className="tw:h-5 tw:rounded-sm" />,
          }))
        : columns,
    [pending, columns],
  )

  const table = useReactTable({
    columns: tableColumns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedRowModel: getFacetedRowModel(),
    getPaginationRowModel:
      paginationType === "client" ? getPaginationRowModel() : undefined,
    onSortingChange,
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange,
    onPaginationChange,
    onColumnVisibilityChange,
    onColumnSizingChange,
    onColumnOrderChange,
    enableRowSelection: true,
    enableColumnResizing,
    columnResizeMode,
    manualPagination: paginationType === "server",
    manualFiltering: paginationType === "server",
    rowCount: useStableValue(rowCount),
    defaultColumn: DEFAULT_COLUMN_CONFIG,
    state: {
      pagination,
      sorting,
      rowSelection,
      columnFilters,
      columnVisibility,
      columnSizing,
      columnOrder,
    },
    filterFns: {
      equalsOneOf: equalsOneOf,
    },
  })

  const { sensors, handleDragEnd } = useDndSetup(
    columnOrder,
    onColumnOrderChange,
  )

  const cellPadding = condensed
    ? "tw:py-0 tw:[&:has(button)]:py-1 tw:px-2"
    : "tw:p-2"

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <div className="tw:flex tw:justify-end tw:gap-2">
        {filters &&
          filters.length > 0 &&
          (paginationType === "server" ? (
            <ServerDataTableToolbar
              table={table}
              filterConfig={filters}
              filterValues={serverFilters ?? {}}
              onFilterChange={onServerFiltersChange ?? (() => {})}
              onReset={onResetServerFilters ?? (() => {})}
            />
          ) : (
            <DataTableToolbar table={table} filters={filters} />
          ))}
        {showViewOptions && <DataTableViewOptions table={table} />}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="tw:overflow-hidden tw:rounded-md tw:border tw:border-gray-200">
          <Table
            className={cn(
              "tw:bg-white",
              enableColumnResizing ? "tw:table-fixed" : "tw:table-auto",
              classes,
            )}
          >
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  key={headerGroup.id}
                  className="tw:border-b tw:border-gray-200"
                >
                  <SortableContext
                    items={columnOrder ?? []}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header) => {
                      return (
                        <DraggableTableHead
                          key={header.id}
                          header={header}
                          cellPadding={cellPadding}
                          variant={variant}
                          enableColumnResizing={enableColumnResizing}
                          columnOrder={columnOrder}
                        />
                      )
                    })}
                  </SortableContext>
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={
                      rowSelection && row.getIsSelected() && "selected"
                    }
                    className={cn(onRowClick && "tw:hover:cursor-pointer")}
                    onClick={() =>
                      onRowClick && !pending && onRowClick(row.original)
                    }
                    variant={variant}
                  >
                    <SortableContext
                      items={columnOrder ?? []}
                      strategy={horizontalListSortingStrategy}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <DragAlongCell
                          key={cell.id}
                          cell={cell}
                          cellPadding={cellPadding}
                          enableColumnResizing={enableColumnResizing}
                        />
                      ))}
                    </SortableContext>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className={`tw:h-8 tw:text-center tw:break-words tw:whitespace-normal ${cellPadding}`}
                  >
                    {noResultsMessage ? noResultsMessage : "No results"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </DndContext>

      {paginationType && <DataTablePagination table={table} />}
    </div>
  )
}
